import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import API from "utils/API";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoanPurposes, PersonalLoan } from "typedef/PersonalLoan";
import useUser from "context/UserCustomer/useUserCustomerContext";

export function usePowerPay() {
  const [loading, setLoading] = useState(false);
  const [personalLoan, setPersonalLoan] = useState<PersonalLoan>();
  const [error, setError] = useState<string | null>(null);
  const [loanPurposes, setLoanPurposes] = useState<Record<string, string[]>>(
    {},
  );
  const navigate = useNavigate();
  const [fetchActive, setFetchActive] = useState(false);
  const { loan: loanContext } = useUser();
  const [searchParams] = useSearchParams();
  const loanIdFromUrl = searchParams.get("loanId");
  //@ts-ignore
  const loan = loanContext?.loan;
  const fetchDeals = async (loanId: string) => {
    setLoading(true);
    setError(null);
    setFetchActive(true);
    try {
      const response = await API.post({
        url: `/power-pay`,
        data: {
          function: "getOffers",
          loanId: loanId,
        },
      });

      if ("data" in response) {
        const bodyData = JSON.parse((response.data as { body: string }).body);
        await getPersonalLoan(bodyData.loanId);

        const isInReview =
          bodyData?.personalLoan?.borrowerSteps?.submitApplication
            ?.applicationStatus === "needs_review";
        if (!isInReview) {
          navigate(`/power-pay?loanId=${bodyData.loanId}`);
        }
      } else {
        setError((response as { message?: string }).message || "Error");
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : "Error";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const saveDealSelected = async (loanId: string, dealId: string) => {
    setLoading(true);
    try {
      const response = await API.post({
        url: `/power-pay`,
        data: {
          function: "saveDealSelected",
          loanId: loanId,
          dealId: dealId,
        },
      });
      await getApplicationStipulations(
        //@ts-ignore
        response?.data?.applicationNumber,
        loanId,
      );
      await getPersonalLoan(loanId);
      setLoading(false);
      if ("error" in response) {
        setError(response.error);
        setLoading(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const getOrSaveLoanPurposes = async (
    type: string,
    loanId: string,
    loanPurpose?: { purpose: string; subPurpose: string },
  ): Promise<Record<string, string[]>> => {
    setLoading(true);
    try {
      const response = await API.post({
        url: `/power-pay`,
        data: {
          function: "getOrSaveLoanPurposes",
          type,
          loanId,
          loanPurpose,
        },
      });
      if ("data" in response) {
        const data = response.data as
          | LoanPurposes
          | { loanPurposes: LoanPurposes };
        const purposesData =
          (data && "loanPurposes" in data ? data.loanPurposes : data) || {};

        if (type === "get") {
          setLoanPurposes(purposesData);
        }

        return purposesData;
      } else {
        throw new Error((response as { message?: string }).message || "Error");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error";
      setError(errorMessage);
      return {};
    } finally {
      setLoading(false);
    }
  };

  const declineOffer = async (loanId: string) => {
    setLoading(true);
    try {
      await API.post({
        url: `/power-pay`,
        data: {
          function: "declineOffer",
          loanId: loanId,
          rejectedBy: "Borrower",
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error";
      setError(errorMessage);
    } finally {
      await getPersonalLoan(loanId);
      setLoading(false);
    }
  };

  const getBrokerById = async (brokerId: string) => {
    try {
      const response = await API.post({
        url: `/power-pay`,
        data: {
          function: "getBrokerById",
          brokerId: brokerId,
        },
      });
      if ("data" in response) {
        return response.data;
      } else {
        throw new Error((response as { message?: string }).message || "Error");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error";
      setError(errorMessage);
    }
  };

  const submitDocument = async (
    loanId: string,
    documentId: number,
    document: string,
  ) => {
    setLoading(true);
    try {
      const response = await API.post({
        url: `/power-pay`,
        data: {
          function: "submitDocument",
          loanId: loanId,
          documentId: documentId,
          document: document,
        },
      });
      if ("data" in response) {
        await getPersonalLoan(loanId);
      } else {
        setError((response as { message?: string }).message || "Error");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error";
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getApplicationStipulations = async (
    applicationNumber: string,
    loanId: string,
  ) => {
    try {
      await API.post({
        url: `/power-pay`,
        data: {
          function: "getApplicationStipulations",
          applicationNumber: applicationNumber,
          loanId: loanId,
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error";
      setError(errorMessage);
    }
  };

  const getPersonalLoan = async (personalLoanId: string) => {
    try {
      const response = await API.post({
        url: `/power-pay`,
        data: {
          function: "getPersonalLoan",
          loanId: personalLoanId,
        },
      });
      if ("data" in response) {
        const parsedData =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;
        setPersonalLoan(parsedData as PersonalLoan);
      } else {
        setError((response as { message?: string }).message || "Error");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error";
      setError(errorMessage);
    }
  };

  const editLoanAmount = async (
    personalLoan: PersonalLoan,
    project_amount: number,
  ) => {
    setLoading(true);
    try {
      const personalLoanId = personalLoan.personalLoanId;
      const response = await API.post<PersonalLoan>({
        url: `/power-pay`,
        data: {
          function: "UpdateApplicationDealSet",
          type: "UpdateApplicationDealSet",
          applicationNumber: personalLoanId,
          body: {
            project_amount: project_amount,
          },
        },
      });
      if ("error" in response) {
        setError(response.error);
      } else {
        await getPersonalLoan(personalLoan.id);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchLoan = async () => {
      if (!loan?.id?.startsWith("200")) {
        await getPersonalLoan(loanIdFromUrl || "");
      }
    };
    fetchLoan();
  }, [loan, fetchActive, loanIdFromUrl]);

  const loadLoanPurposes = useCallback(async (loanId: string) => {
    try {
      await getOrSaveLoanPurposes("get", loanId);
    } catch (error) {
      console.error("Error loading loan purposes:", error);
    }
  }, []);

  useEffect(() => {
    if (personalLoan?.id && Object.keys(loanPurposes).length === 0) {
      loadLoanPurposes(personalLoan.id);
    }
  }, [personalLoan?.id, loanPurposes, loadLoanPurposes]);

  useEffect(() => {
    if (!personalLoan?.id) return;

    const interval = setInterval(() => {
      getPersonalLoan(personalLoan.id);
    }, 5000);

    return () => clearInterval(interval);
  }, [personalLoan?.id]);

  return {
    loading,
    personalLoan,
    error,
    fetchDeals,
    editLoanAmount,
    saveDealSelected,
    submitDocument,
    getApplicationStipulations,
    getBrokerById,
    declineOffer,
    getLoanPurposes: getOrSaveLoanPurposes,
    loanPurposes,
  };
}

const PowerPayContext = createContext<ReturnType<typeof usePowerPay> | null>(
  null,
);

export const PowerPayProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = usePowerPay();
  return (
    <PowerPayContext.Provider value={value}>
      {children}
    </PowerPayContext.Provider>
  );
};

export const usePowerPayContext = () => {
  const ctx = useContext(PowerPayContext);
  if (!ctx)
    throw new Error("usePowerPayContext must be used within PowerPayProvider");
  return ctx;
};
